import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getSupabase, berserkr, isSupabaseConfigured } from "../lib/supabase";

export type RavenSubjectType =
  | "opposing_party"
  | "opposing_counsel"
  | "assigned_judge"
  | "adjacent_party"
  | "expert_witness"
  | "other";

export interface RavenCitation {
  url: string;
  title: string;
  snippet?: string;
}

export interface RavenDossier {
  id: string;
  case_id: string;
  subject_type: RavenSubjectType;
  subject_name: string;
  subject_context: string | null;
  summary: string | null;
  body: Record<string, unknown>;
  citations: RavenCitation[];
  llm_model: string | null;
  dispatched_at: string;
}

/**
 * List Raven dossiers compiled for a given case, newest first.
 */
export function useRavenDossiers(caseId: string | undefined) {
  return useQuery<RavenDossier[], Error>({
    queryKey: ["raven-dossiers", caseId],
    enabled: isSupabaseConfigured() && !!caseId,
    queryFn: async () => {
      if (!caseId) return [];
      const { data, error } = await berserkr()
        .from("raven_dossiers")
        .select(
          "id, case_id, subject_type, subject_name, subject_context, summary, body, citations, llm_model, dispatched_at",
        )
        .eq("case_id", caseId)
        .order("dispatched_at", { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as RavenDossier[];
    },
  });
}

export interface DispatchInput {
  caseId: string;
  caseCaption?: string;
  caseJurisdiction?: string;
  caseNumber?: string;
  caseCourt?: string;
  /** Tell the Raven the parties so it can identify counsel/judge by case. */
  caseParties?: Array<{
    display_name: string;
    role: string;
    capacities?: string[];
  }>;
  subjectType: RavenSubjectType;
  /** If empty, Raven will identify the subject itself from case context. */
  subjectName?: string;
  subjectContext?: string;
}

interface DispatchResult {
  summary: string;
  body: Record<string, unknown>;
  citations: RavenCitation[];
  llm_model: string;
}

/**
 * Two-step dispatch: call the dispatch-raven Edge Function to compile
 * the dossier (slow — web search + LLM), then call save_raven_dossier
 * RPC to persist it. The mutation returns the new dossier id.
 */
export function useDispatchRaven() {
  const queryClient = useQueryClient();

  return useMutation<string, Error, DispatchInput>({
    mutationFn: async (input) => {
      const supabase = getSupabase();
      if (!supabase) throw new Error("Supabase not configured");

      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error("Not signed in");

      const baseUrl = import.meta.env.VITE_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

      // 1. Compile the dossier via the Edge Function.
      const res = await fetch(`${baseUrl}/functions/v1/dispatch-raven`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          apikey: anonKey,
        },
        body: JSON.stringify({
          subject_type: input.subjectType,
          subject_name: input.subjectName ?? "",
          subject_context: input.subjectContext,
          case_caption: input.caseCaption,
          case_jurisdiction: input.caseJurisdiction,
          case_number: input.caseNumber,
          case_court: input.caseCourt,
          case_parties: input.caseParties,
        }),
      });

      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const parsed = JSON.parse(text);
          detail = parsed.error ?? text;
        } catch {}
        throw new Error(`Raven dispatch failed (HTTP ${res.status}): ${detail}`);
      }

      const result = JSON.parse(text) as DispatchResult;

      // The Edge Function may have identified the subject if name was blank;
      // prefer the resolved name from the dossier body if present.
      const resolvedSubjectName =
        typeof result.body?.resolved_subject_name === "string" &&
        (result.body.resolved_subject_name as string).trim().length > 0
          ? (result.body.resolved_subject_name as string).trim()
          : input.subjectName?.trim() || "(unidentified)";

      // 2. Persist via RPC.
      const { data: dossierId, error: rpcError } = await berserkr().rpc(
        "save_raven_dossier",
        {
          p_case_id: input.caseId,
          p_subject_type: input.subjectType,
          p_subject_name: resolvedSubjectName,
          p_subject_context: input.subjectContext ?? null,
          p_summary: result.summary,
          p_body: result.body,
          p_citations: result.citations,
          p_llm_model: result.llm_model,
        },
      );

      if (rpcError) {
        throw new Error(`Save dossier failed: ${rpcError.message}`);
      }
      if (!dossierId) {
        throw new Error("Save dossier returned no id");
      }

      return dossierId as string;
    },
    onSuccess: (_id, vars) => {
      queryClient.invalidateQueries({
        queryKey: ["raven-dossiers", vars.caseId],
      });
    },
  });
}
